import React, { useState, useEffect, useRef, useCallback } from 'react';
import { updateGame, upsertInningScore, logEvent, getInningScores } from '../gameEngine';

function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2500);
    return () => clearTimeout(t);
  }, [onDone]);
  return <div className="toast">{message}</div>;
}

export default function UmpireView({ game: initialGame, navigate }) {
  const [game, setGame] = useState(initialGame);
  const [inningScores, setInningScores] = useState({});
  const [toast, setToast] = useState(null);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [timerDisplay, setTimerDisplay] = useState('');
  const [timerRunning, setTimerRunning] = useState(false);
  const timerRef = useRef(null);
  const historyRef = useRef([]);

  // Load inning scores
  useEffect(() => {
    getInningScores(game.id).then(rows => {
      const scores = {};
      rows.forEach(r => {
        if (!scores[r.inning]) scores[r.inning] = {};
        scores[r.inning][r.team] = r.runs;
      });
      setInningScores(scores);
    });
  }, [game.id]);

  // Timer logic
  useEffect(() => {
    if (!game.time_limit_enabled) return;
    const tick = () => {
      const totalSecs = game.time_limit_minutes * 60;
      let elapsed = 0;
      if (timerRunning && game.timer_started_at) {
        elapsed = Math.floor((Date.now() - new Date(game.timer_started_at).getTime()) / 1000);
      }
      const remaining = Math.max(0, totalSecs - elapsed);
      const m = Math.floor(remaining / 60);
      const s = remaining % 60;
      setTimerDisplay(`${m}:${s.toString().padStart(2, '0')}`);
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [game.time_limit_enabled, game.time_limit_minutes, game.timer_started_at, timerRunning]);

  const showToast = (msg) => setToast(msg);

  const saveSnapshot = useCallback(() => {
    historyRef.current.push(JSON.stringify(game));
    if (historyRef.current.length > 50) historyRef.current.shift();
  }, [game]);

  const applyAndSave = useCallback(async (updates) => {
    const updated = { ...game, ...updates };
    setGame(updated);
    try {
      await updateGame(game.id, updates);
    } catch (e) {
      console.error('Save error:', e);
    }
    return updated;
  }, [game]);

  const checkMercyInning = (g) => g.mercy_enabled && g.inning_run_count >= g.mercy_runs;
  const checkGameMercy = (g) => {
    if (!g.game_mercy_enabled) return false;
    return Math.abs(g.home_score - g.away_score) >= g.game_mercy_runs && g.current_inning > 1;
  };

  const endHalfInning = (g) => {
    const next = {
      ...g,
      outs: 0, balls: 0, strikes: 0,
      base_1: false, base_2: false, base_3: false,
      inning_run_count: 0,
    };
    if (!g.is_top_inning) {
      if (g.max_innings !== 99 && g.current_inning >= g.max_innings) {
        return { ...next, status: 'final', winner: g.home_score > g.away_score ? 'home' : g.away_score > g.home_score ? 'away' : 'tie' };
      }
      next.current_inning = g.current_inning + 1;
    }
    next.is_top_inning = !g.is_top_inning;
    return next;
  };

  const endGame = (g, reason = 'final') => ({
    ...g,
    status: 'final',
    winner: g.home_score > g.away_score ? 'home' : g.away_score > g.home_score ? 'away' : 'tie',
    ended_at: new Date().toISOString(),
  });

  // Actions
  const handleStrike = async () => {
    saveSnapshot();
    const newStrikes = game.strikes + 1;
    if (newStrikes >= game.max_strikes) {
      let g = { ...game, strikes: 0, balls: 0, outs: game.outs + 1, base_1: false, base_2: false, base_3: false };
      showToast('Strike Out! ❌');
      await logEvent(game.id, 'strikeout', 'Strike out', game.current_inning, game.is_top_inning);
      if (g.outs >= g.max_outs) g = endHalfInning(g);
      await applyAndSave(g);
    } else {
      await applyAndSave({ strikes: newStrikes });
      await logEvent(game.id, 'strike', `Strike ${newStrikes}`, game.current_inning, game.is_top_inning);
    }
  };

  const handleBall = async () => {
    saveSnapshot();
    const newBalls = game.balls + 1;
    if (newBalls >= game.max_balls) {
      // Walk — advance runners forced
      let { base_1, base_2, base_3, home_score, away_score, inning_run_count } = game;
      const battingTeam = game.is_top_inning ? 'away' : 'home';
      if (base_1 && base_2 && base_3) {
        if (battingTeam === 'home') {
          home_score++;
          await upsertInningScore(game.id, game.current_inning, 'home', (inningScores[game.current_inning]?.home || 0) + 1);
        } else {
          away_score++;
          await upsertInningScore(game.id, game.current_inning, 'away', (inningScores[game.current_inning]?.away || 0) + 1);
        }
        inning_run_count++;
      }
      if (base_1 && base_2) base_3 = true;
      if (base_1) base_2 = true;
      base_1 = true;
      showToast('Walk — Batter to 1st 🟢');
      await logEvent(game.id, 'walk', 'Walk', game.current_inning, game.is_top_inning);
      await applyAndSave({ balls: 0, strikes: 0, base_1, base_2, base_3, home_score, away_score, inning_run_count });
    } else {
      await applyAndSave({ balls: newBalls });
    }
  };

  const handleOut = async () => {
    saveSnapshot();
    const newOuts = game.outs + 1;
    await logEvent(game.id, 'out', `Out ${newOuts}`, game.current_inning, game.is_top_inning);
    if (newOuts >= game.max_outs) {
      const g = endHalfInning({ ...game, outs: newOuts, balls: 0, strikes: 0 });
      if (checkGameMercy(g)) { await applyAndSave(endGame(g)); showToast('Game Mercy Rule 🏆'); }
      else await applyAndSave(g);
    } else {
      await applyAndSave({ outs: newOuts, balls: 0, strikes: 0, base_1: false, base_2: false, base_3: false });
    }
  };

  const handleRun = async (team) => {
    saveSnapshot();
    setShowRunModal(false);
    const updates = {};
    if (team === 'home') {
      updates.home_score = game.home_score + 1;
      const runs = (inningScores[game.current_inning]?.home || 0) + 1;
      await upsertInningScore(game.id, game.current_inning, 'home', runs);
      setInningScores(s => ({ ...s, [game.current_inning]: { ...s[game.current_inning], home: runs } }));
    } else {
      updates.away_score = game.away_score + 1;
      const runs = (inningScores[game.current_inning]?.away || 0) + 1;
      await upsertInningScore(game.id, game.current_inning, 'away', runs);
      setInningScores(s => ({ ...s, [game.current_inning]: { ...s[game.current_inning], away: runs } }));
    }
    updates.inning_run_count = game.inning_run_count + 1;
    const teamName = team === 'home' ? game.home_team_name : game.away_team_name;
    showToast(`Run scored — ${teamName}! 🏃`);
    await logEvent(game.id, 'run', `Run — ${teamName}`, game.current_inning, game.is_top_inning);
    const g = { ...game, ...updates };
    if (checkGameMercy(g)) { await applyAndSave(endGame(g)); showToast('Game Mercy Rule 🏆'); }
    else if (checkMercyInning(g)) { await applyAndSave(endHalfInning(g)); showToast('Inning Mercy Rule!'); }
    else await applyAndSave(updates);
  };

  const handleBaseHit = async () => {
    saveSnapshot();
    // Advance runners — single: everyone moves up one base
    let { base_1, base_2, base_3, home_score, away_score, inning_run_count } = game;
    const battingTeam = game.is_top_inning ? 'away' : 'home';
    // Runner on 3rd scores
    if (base_3) {
      if (battingTeam === 'home') {
        home_score++;
        await upsertInningScore(game.id, game.current_inning, 'home', (inningScores[game.current_inning]?.home || 0) + 1);
      } else {
        away_score++;
        await upsertInningScore(game.id, game.current_inning, 'away', (inningScores[game.current_inning]?.away || 0) + 1);
      }
      inning_run_count++;
    }
    base_3 = base_2;
    base_2 = base_1;
    base_1 = true;
    showToast('Base Hit! 🏃');
    await logEvent(game.id, 'hit', 'Base hit', game.current_inning, game.is_top_inning);
    const g = { ...game, base_1, base_2, base_3, home_score, away_score, inning_run_count, balls: 0, strikes: 0 };
    if (checkGameMercy(g)) { await applyAndSave(endGame(g)); showToast('Game Mercy Rule 🏆'); }
    else if (checkMercyInning(g)) { await applyAndSave(endHalfInning(g)); showToast('Inning Mercy Rule!'); }
    else await applyAndSave({ base_1, base_2, base_3, home_score, away_score, inning_run_count, balls: 0, strikes: 0 });
  };

  const handleNextBatter = async () => {
    saveSnapshot();
    await applyAndSave({ balls: 0, strikes: 0 });
  };

  const handleToggleBase = async (base) => {
    saveSnapshot();
    const key = `base_${base}`;
    await applyAndSave({ [key]: !game[key] });
  };

  const handleUndo = async () => {
    if (!historyRef.current.length) return;
    const snap = JSON.parse(historyRef.current.pop());
    setGame(snap);
    await updateGame(game.id, snap);
    showToast('↩️ Undone');
  };

  const handleEndGame = async (reason) => {
    setShowEndModal(false);
    const g = endGame(game);
    let desc = 'Game ended';
    if (reason === 'rain') desc = 'Game called — rain delay';
    if (reason === 'forfeit') desc = 'Game ended — forfeit';
    await logEvent(game.id, 'game_end', desc, game.current_inning, game.is_top_inning);
    await applyAndSave(g);
  };

  const handleTimerToggle = async () => {
    if (!timerRunning) {
      const now = new Date().toISOString();
      setTimerRunning(true);
      await updateGame(game.id, { timer_running: true, timer_started_at: now });
      setGame(g => ({ ...g, timer_started_at: now, timer_running: true }));
    } else {
      setTimerRunning(false);
      await updateGame(game.id, { timer_running: false });
    }
  };

  const handleShare = async () => {
    const text = `Join my UmpBuddy game! Code: ${game.short_code}\numpbuddy.com`;
    if (navigator.share) {
      await navigator.share({ title: 'UmpBuddy', text });
    } else {
      await navigator.clipboard.writeText(text);
      showToast('Game code copied! 📋');
    }
  };

  if (game.status === 'final') {
    const winner = game.winner === 'home' ? game.home_team_name : game.winner === 'away' ? game.away_team_name : null;
    return (
      <div className="screen">
        <div className="header">
          <span className="header-title">Game Over</span>
        </div>
        <div className="game-over">
          <div className="game-over-title">Final Score</div>
          <div className="game-over-score">{game.away_score} – {game.home_score}</div>
          <div className="game-over-teams">
            <span>{game.away_team_name}</span>
            <span style={{ color: 'var(--border)' }}>vs</span>
            <span>{game.home_team_name}</span>
          </div>
          {winner && <div className="game-over-winner">🏆 {winner} Wins!</div>}
          {game.winner === 'tie' && <div className="game-over-winner">It's a Tie!</div>}
          <div style={{ marginTop: 24, width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button className="btn btn-primary" onClick={() => navigate('newGame')}>⚾ New Game</button>
            <button className="btn btn-ghost" onClick={() => navigate('home')}>Home</button>
          </div>
        </div>
      </div>
    );
  }

  const battingTeam = game.is_top_inning ? game.away_team_name : game.home_team_name;
  const inningLabel = `${game.is_top_inning ? '▲ TOP' : '▼ BOT'} ${game.current_inning}`;
  const timerWarning = timerDisplay && parseInt(timerDisplay) < 5;

  return (
    <div className="screen">
      {/* Header */}
      <div className="header">
        <span className="header-title">⚾ UmpBuddy</span>
        <button className="btn btn-sm btn-danger" style={{ width: 'auto', padding: '6px 14px', fontSize: 13 }}
          onClick={() => setShowEndModal(true)}>End Game</button>
      </div>

      {/* Game code bar */}
      <div className="game-code-bar">
        <div>
          <div className="game-code-label">Game Code</div>
          <div className="game-code-value">{game.short_code}</div>
        </div>
        <button className="game-code-share" onClick={handleShare}>Share 📤</button>
      </div>

      <div className="scroll-content">
        {/* Scoreboard */}
        <div className="scoreboard">
          <div className="scoreboard-inning">{inningLabel} &nbsp;·&nbsp; {battingTeam} Batting</div>
          <div className="scoreboard-teams">
            <div className="scoreboard-row">
              <span className="team-label">AWAY</span>
              <span className="team-name">{game.away_team_name}</span>
              <span className={`team-score ${game.is_top_inning ? 'batting' : ''}`}>{game.away_score}</span>
            </div>
            <div className="scoreboard-row">
              <span className="team-label">HOME</span>
              <span className="team-name">{game.home_team_name}</span>
              <span className={`team-score ${!game.is_top_inning ? 'batting' : ''}`}>{game.home_score}</span>
            </div>
          </div>
        </div>

        {/* Timer */}
        {game.time_limit_enabled && (
          <div className="timer-bar">
            <div>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--gray)', fontWeight: 700, marginBottom: 2 }}>Time Remaining</div>
              <div className={`timer-display ${timerWarning ? 'warning' : ''}`}>{timerDisplay || `${game.time_limit_minutes}:00`}</div>
            </div>
            <div className="timer-controls">
              <button className={`timer-btn ${timerRunning ? 'running' : ''}`} onClick={handleTimerToggle}>
                {timerRunning ? '⏸ Pause' : '▶ Start'}
              </button>
            </div>
          </div>
        )}

        {/* Count */}
        <div className="count-bar">
          <div className="count-item">
            <span className="count-label">Balls</span>
            <span className="count-val balls">{game.balls}</span>
          </div>
          <div className="count-item">
            <span className="count-label">Strikes</span>
            <span className="count-val strikes">{game.strikes}</span>
          </div>
          <div className="count-item">
            <span className="count-label">Outs</span>
            <span className="count-val outs">{game.outs}</span>
          </div>
        </div>

        {/* Bases */}
        <div className="bases-container">
          <div className="bases-title">Bases</div>
          <div className="bases-diamond">
            <div className={`base base-2nd ${game.base_2 ? 'occupied' : ''}`} onClick={() => handleToggleBase(2)} />
            <div className={`base base-3rd ${game.base_3 ? 'occupied' : ''}`} onClick={() => handleToggleBase(3)} />
            <div className={`base base-1st ${game.base_1 ? 'occupied' : ''}`} onClick={() => handleToggleBase(1)} />
            <div className="base base-home" />
          </div>
        </div>

        {/* Action buttons */}
        <div className="action-grid">
          <button className="action-btn action-strike" onClick={handleStrike}>
            STRIKE
            <span className="action-sub">{game.strikes + 1} of {game.max_strikes}</span>
          </button>
          <button className="action-btn action-ball" onClick={handleBall}>
            BALL
            <span className="action-sub">{game.balls + 1} of {game.max_balls}</span>
          </button>
          <button className="action-btn action-out" onClick={handleOut}>
            OUT
            <span className="action-sub">{game.outs + 1} of {game.max_outs}</span>
          </button>
          <button className="action-btn action-run" onClick={() => setShowRunModal(true)}>
            RUN
            <span className="action-sub">Score a run</span>
          </button>
          <button className="action-btn action-next" onClick={handleNextBatter}>
            NEXT
            <span className="action-sub">Next batter</span>
          </button>
          <button className="action-btn action-run" style={{ background: '#0a2200', color: '#8bc34a', borderColor: '#2a4a0a' }} onClick={handleBaseHit}>
            HIT
            <span className="action-sub">Base hit</span>
          </button>
          <button className="action-btn action-undo" onClick={handleUndo}>
            UNDO
            <span className="action-sub">Last action</span>
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}

      {/* Run Modal */}
      {showRunModal && (
        <div className="modal-overlay" onClick={() => setShowRunModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Who Scored?</div>
            <button className="btn btn-primary" onClick={() => handleRun(game.is_top_inning ? 'away' : 'home')}>
              {game.is_top_inning ? game.away_team_name : game.home_team_name} (Batting)
            </button>
            <button className="btn btn-secondary" onClick={() => handleRun(game.is_top_inning ? 'home' : 'away')}>
              {game.is_top_inning ? game.home_team_name : game.away_team_name}
            </button>
            <button className="btn btn-ghost" onClick={() => setShowRunModal(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* End Game Modal */}
      {showEndModal && (
        <div className="modal-overlay" onClick={() => setShowEndModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">End Game</div>
            <div className="modal-sub">How is the game ending?</div>
            <button className="btn btn-primary" onClick={() => handleEndGame('final')}>⚾ Game Complete</button>
            <button className="btn btn-secondary" onClick={() => handleEndGame('rain')}>🌧 Rain Delay / Called</button>
            <button className="btn btn-secondary" onClick={() => handleEndGame('forfeit')}>🏳 Forfeit</button>
            <button className="btn btn-ghost" onClick={() => setShowEndModal(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
